<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { UserImportsApi } from 'dashboard/api/orgAccess';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();

const fileInput = ref(null);
const selectedFile = ref(null);
const isRunning = ref(false);
const response = ref(null);
const errorMessage = ref('');

const TEMPLATE_ROWS = [
  ['name', 'email', 'password', 'unit_path', 'role', 'inboxes'],
  ['أحمد محمود', 'ahmed@example.com', '', 'المبيعات', 'director', ''],
  [
    'سارة إبراهيم',
    'sara@example.com',
    '',
    'المبيعات / إدارة القاهرة',
    'manager',
    '',
  ],
  [
    'كريم عادل',
    'karim@example.com',
    '',
    'المبيعات / إدارة القاهرة / فريق أ',
    'team_leader',
    'واتساب المبيعات',
  ],
  [
    'منى حسن',
    'mona@example.com',
    '',
    'المبيعات / إدارة القاهرة / فريق أ',
    'agent',
    'واتساب المبيعات',
  ],
];

const createdWithPasswords = computed(
  () => response.value?.results?.filter(row => row.password) || []
);

const statusClass = status => {
  if (status === 'CREATED') return 'text-n-teal-11';
  if (status === 'UPDATED') return 'text-n-slate-12';
  if (status === 'FAILED') return 'text-n-ruby-11';
  return 'text-n-slate-11';
};

// A BOM keeps Excel from mangling Arabic when the file is opened.
const downloadCsv = (rows, filename) => {
  const body = rows
    .map(row =>
      row.map(cell => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(',')
    )
    .join('\r\n');
  const blob = new Blob([`\uFEFF${body}`], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
};

const downloadTemplate = () => downloadCsv(TEMPLATE_ROWS, 'users-template.csv');

const downloadPasswords = () =>
  downloadCsv(
    [
      ['email', 'password'],
      ...createdWithPasswords.value.map(row => [row.email, row.password]),
    ],
    'users-passwords.csv'
  );

const onFileChange = event => {
  selectedFile.value = event.target.files?.[0] || null;
  response.value = null;
  errorMessage.value = '';
};

const run = async dryRun => {
  if (!selectedFile.value) return;
  isRunning.value = true;
  errorMessage.value = '';
  try {
    const { data } = await UserImportsApi.upload(selectedFile.value, dryRun);
    response.value = data;
    if (!dryRun) useAlert(t('ORG_ACCESS.IMPORT.DONE'));
  } catch (error) {
    errorMessage.value =
      error?.response?.data?.error || error.message || 'Unknown error';
  } finally {
    isRunning.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-5 max-w-4xl">
    <div class="flex flex-col gap-2">
      <p class="text-sm text-n-slate-11">
        {{ t('ORG_ACCESS.IMPORT.DESCRIPTION') }}
      </p>
      <div>
        <Button
          size="sm"
          variant="outline"
          :label="t('ORG_ACCESS.IMPORT.DOWNLOAD_TEMPLATE')"
          @click="downloadTemplate"
        />
      </div>
    </div>

    <div class="flex flex-col gap-3 p-4 border rounded-lg border-n-weak">
      <label class="flex flex-col gap-2 text-sm text-n-slate-12">
        {{ t('ORG_ACCESS.IMPORT.PICK_FILE') }}
        <input
          ref="fileInput"
          type="file"
          accept=".csv,text/csv"
          @change="onFileChange"
        />
      </label>
      <p class="text-xs text-n-slate-11">
        {{ t('ORG_ACCESS.IMPORT.EXCEL_HINT') }}
      </p>

      <div class="flex flex-wrap gap-2">
        <Button
          variant="outline"
          :disabled="!selectedFile || isRunning"
          :is-loading="isRunning"
          :label="t('ORG_ACCESS.IMPORT.DRY_RUN')"
          @click="run(true)"
        />
        <Button
          :disabled="!selectedFile || isRunning"
          :is-loading="isRunning"
          :label="t('ORG_ACCESS.IMPORT.RUN')"
          @click="run(false)"
        />
      </div>
    </div>

    <div
      v-if="errorMessage"
      class="p-3 text-sm border rounded-lg text-n-ruby-11 border-n-ruby-8"
    >
      {{ errorMessage }}
    </div>

    <div v-if="response" class="flex flex-col gap-3">
      <div class="flex flex-wrap items-center gap-3">
        <span
          v-if="response.dry_run"
          class="px-2 py-1 text-xs rounded bg-n-amber-3 text-n-amber-11"
        >
          {{ t('ORG_ACCESS.IMPORT.DRY_RUN_BADGE') }}
        </span>
        <span class="text-sm text-n-slate-12">
          {{
            t('ORG_ACCESS.IMPORT.SUMMARY', {
              created: response.summary.created,
              updated: response.summary.updated,
              skipped: response.summary.skipped,
              failed: response.summary.failed,
            })
          }}
        </span>
        <Button
          v-if="createdWithPasswords.length"
          size="sm"
          color="teal"
          :label="t('ORG_ACCESS.IMPORT.DOWNLOAD_PASSWORDS')"
          @click="downloadPasswords"
        />
      </div>

      <div class="overflow-x-auto border rounded-lg border-n-weak">
        <table class="w-full text-sm">
          <thead class="bg-n-alpha-2 text-n-slate-11">
            <tr>
              <th class="px-3 py-2 text-start">#</th>
              <th class="px-3 py-2 text-start">
                {{ t('ORG_ACCESS.IMPORT.COL_EMAIL') }}
              </th>
              <th class="px-3 py-2 text-start">
                {{ t('ORG_ACCESS.IMPORT.COL_STATUS') }}
              </th>
              <th class="px-3 py-2 text-start">
                {{ t('ORG_ACCESS.IMPORT.COL_DETAILS') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in response.results"
              :key="`${row.line}-${row.email}`"
              class="border-t border-n-weak"
            >
              <td class="px-3 py-2 text-n-slate-11">{{ row.line }}</td>
              <td class="px-3 py-2 text-n-slate-12">{{ row.email }}</td>
              <td
                class="px-3 py-2 font-medium"
                :class="statusClass(row.status)"
              >
                {{ row.status }}
              </td>
              <td class="px-3 py-2 text-n-slate-11">{{ row.message }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p v-if="createdWithPasswords.length" class="text-xs text-n-amber-11">
        {{ t('ORG_ACCESS.IMPORT.PASSWORD_WARNING') }}
      </p>
    </div>
  </div>
</template>
